import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plus, Users, Trash2, Edit, ArrowLeft, Save, Star, MessageCircle,
  Phone, Mail, MapPin, Package
} from "lucide-react";
import { toast } from "sonner";
import CallPrompt from "@/pages/trade/CallPrompt";
import ContactAudioRecordButton from "@/pages/trade/ContactAudioRecordButton";
import RecordedCallButton from "@/pages/trade/RecordedCallButton";

export const SuppliersList = () => {
  const { API, authHeader } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    axios.get(`${API}/trade/suppliers`, { headers: authHeader() })
      .then(res => setSuppliers(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this supplier?")) return;
    await axios.delete(`${API}/trade/suppliers/${id}`, { headers: authHeader() });
    toast.success("Supplier deleted");
    fetchData();
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between flex-wrap gap-4 mb-10"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Suppliers</p>
            <h1 className="text-4xl md:text-5xl font-serif font-bold">Your China Network</h1>
            <p className="text-white/50 mt-2">Track and manage your suppliers</p>
          </div>
          <Link to="/trade/suppliers/new">
            <Button className="trade-btn-primary px-6 py-6 rounded-sm">
              <Plus size={18} className="mr-2" /> New Supplier
            </Button>
          </Link>
        </motion.div>

        {loading ? (
          <div className="text-center p-20 text-white/40">Loading...</div>
        ) : suppliers.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            <Users className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No suppliers yet</h3>
            <p className="text-white/50 mb-6">Add suppliers to track your business relationships</p>
            <Link to="/trade/suppliers/new">
              <Button className="trade-btn-primary rounded-sm">
                <Plus size={16} className="mr-2" /> Add Supplier
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {suppliers.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link to={`/trade/suppliers/${s.id}`} className="group block trade-card rounded-sm p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
                      <Users className="w-6 h-6 trade-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, idx) => (
                        <Star key={idx} size={12} className={idx < (s.rating || 0) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"} />
                      ))}
                    </div>
                  </div>
                  
                  <h3 className="font-serif font-semibold text-lg mb-1">{s.name}</h3>
                  {s.city && <p className="text-xs text-white/50 flex items-center gap-1 mb-3"><MapPin size={10} /> {s.city}</p>}
                  
                  {s.products && (
                    <p className="text-xs text-white/60 mb-4 line-clamp-2 flex items-start gap-1">
                      <Package size={10} className="mt-0.5 flex-shrink-0" /> {s.products}
                    </p>
                  )}

                  <div className="space-y-2 pt-4 border-t border-white/5">
                    {s.contact_person && <p className="text-xs text-white/70">{s.contact_person}</p>}
                    {s.phone && <p className="text-xs text-white/50 flex items-center gap-2"><Phone size={10} /> {s.phone}</p>}
                    {s.whatsapp && <p className="text-xs text-white/50 flex items-center gap-2"><span className="text-green-400">💬</span> {s.whatsapp}</p>}
                    {s.wechat && <p className="text-xs text-white/50 flex items-center gap-2"><MessageCircle size={10} /> {s.wechat}</p>}
                  </div>

                  <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                    {s.phone && (
                      <CallPrompt
                        phone={s.phone}
                        name={s.name}
                        className="flex-1 text-xs bg-white/5 text-white/70 hover:bg-white/10 px-3 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                        testid={`call-supplier-${s.id}`}
                      />
                    )}
                    <ContactAudioRecordButton
                      phone={s.phone}
                      name={s.name}
                      className="flex-1 text-xs bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 px-3 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                      testid={`audio-supplier-${s.id}`}
                    />
                    {s.phone && (
                      <RecordedCallButton
                        phone={s.phone}
                        name={s.name}
                        contactId={s.id}
                        contactType="supplier"
                        className="flex-1 text-xs bg-red-500/10 text-red-300 hover:bg-red-500/20 px-3 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                        testid={`record-call-supplier-${s.id}`}
                      />
                    )}
                    <button
                      onClick={(e) => handleDelete(s.id, e)}
                      className="text-xs text-red-400/60 hover:text-red-400 transition-colors flex items-center gap-1 ml-auto"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const SupplierForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API, authHeader } = useAuth();
  const isEdit = !!id && id !== "new";

  const [form, setForm] = useState({
    name: "", contact_person: "", phone: "", email: "", wechat: "", whatsapp: "",
    city: "", products: "", notes: "", rating: 5
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/trade/suppliers`, { headers: authHeader() })
        .then(res => {
          const found = res.data.find(s => s.id === id);
          if (found) setForm(found);
        });
    }
  }, [id]);

  const handleSave = async () => {
    if (!form.name) {
      toast.error("Supplier name is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/trade/suppliers/${id}`, form, { headers: authHeader() });
        toast.success("Supplier updated");
      } else {
        await axios.post(`${API}/trade/suppliers`, form, { headers: authHeader() });
        toast.success("Supplier created");
      }
      navigate("/trade/suppliers");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-3xl">
        <Link to="/trade/suppliers" className="inline-flex items-center gap-2 text-sm text-white/60 hover:trade-gold mb-6">
          <ArrowLeft size={16} /> Back
        </Link>
        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-8">
          {isEdit ? "Edit" : "New"} Supplier
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="trade-card rounded-sm p-8 space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Company Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Contact Person</Label>
              <Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">City</Label>
              <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="e.g. Guangzhou" className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Phone</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">WhatsApp</Label>
              <Input value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} placeholder="+86 123 4567" className="trade-input mt-1 h-11 rounded-sm" data-testid="supplier-whatsapp-input" />
            </div>
            <div>
              <Label className="text-xs text-white/60">WeChat ID</Label>
              <Input value={form.wechat} onChange={e => setForm({...form, wechat: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Email</Label>
              <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Products</Label>
              <Input value={form.products} onChange={e => setForm({...form, products: e.target.value})} placeholder="e.g. Electronics, textiles" className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    onClick={() => setForm({...form, rating: n})}
                    className="transition-transform hover:scale-110"
                  >
                    <Star size={24} className={n <= form.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"} />
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={4} className="trade-input mt-1 rounded-sm" />
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full trade-btn-primary rounded-sm h-12">
            <Save size={16} className="mr-2" />
            {saving ? "Saving..." : (isEdit ? "Update" : "Create") + " Supplier"}
          </Button>
        </motion.div>
      </div>
    </div>
  );
};
